import React, { useMemo } from 'react';
import { useIntl, type MessageDescriptor } from 'react-intl';
import { PRESS_PHOTO_VARIANTS, profileImageSpec, type PressPhotoVariant } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import type { HeaderPreviewGround } from '../../studio/spotifyHeader/FrameGround';
import { ProfileImageFrame } from '../frame/ProfileImageFrame';
import { profileFileGround } from '../profileAssets';
import { slotsChip } from '../profileChips';
import { profileSlotsOf } from '../profileSlots';
import { profileMessages as m } from '../profileMessages';
import { ProfileCard } from './ProfileCard';
import { ProfileChipBadge } from './ProfileChipBadge';
import { ProfileSlotNote } from './ProfileSlotNote';
import type { ProfileCardProps } from './profileCardProps';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The three crops on the card (design a1): the label and the width of each preview. */
const CROPS: Readonly<Record<PressPhotoVariant, { label: MessageDescriptor; width: number }>> = {
  landscape: { label: m.pressLandscape, width: 150 },
  portrait: { label: m.pressPortrait, width: 96 },
  square: { label: m.pressSquare, width: 120 },
};

/** The press photos card (design a1, a2): one photo, three crops; Edit (Make them when not made). */
const ProfilePressCardComponent: React.FC<{ card: ProfileCardProps }> = ({ card }) => {
  const { profile, colours, openTool, retry, isMaking } = card;
  const intl = useIntl();
  const slots = useMemo(() => profileSlotsOf(profile.assets, 'pressPhoto', PRESS_PHOTO_VARIANTS), [profile.assets]);
  const made = slots.some((slot) => slot.current !== null);

  return (
    <ProfileCard
      service="press"
      name={intl.formatMessage(m.pressName)}
      size={intl.formatMessage(m.pressSize)}
      chip={<ProfileChipBadge chip={slotsChip(slots)} testId="profile-chip-pressPhoto" />}
      testId="profile-card-pressPhoto"
    >
      <div className={styles.stage}>
        <div className={styles.press}>
          {slots.map((slot) => {
            const variant = slot.variant as PressPhotoVariant;
            const ground: HeaderPreviewGround = profileFileGround(slot.current) ?? { kind: 'theme' };
            return (
              <div key={variant} className={styles.stageCol}>
                <ProfileImageFrame
                  spec={profileImageSpec('pressPhoto', variant)}
                  width={CROPS[variant].width}
                  ground={ground}
                  colours={colours}
                  testId={`profile-frame-press-${variant}`}
                />
                <span className={styles.pressLabel}>{intl.formatMessage(CROPS[variant].label)}</span>
              </div>
            );
          })}
        </div>
      </div>
      <ProfileSlotNote
        slots={slots}
        name={intl.formatMessage(m.pressName)}
        onTryAgain={() => retry(slots)}
        isBusy={isMaking}
      />
      <div className={styles.row}>
        <SMLButton className={styles.sm} onClick={() => openTool('pressPhoto')}>
          {intl.formatMessage(made ? m.edit : m.makeThem)}
        </SMLButton>
        <span className={styles.grow} />
        <span className={styles.meta}>{intl.formatMessage(m.pressMeta)}</span>
      </div>
    </ProfileCard>
  );
};

export const ProfilePressCard = TypedMemo(ProfilePressCardComponent);
